import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import api from '../services/api';

interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
}

interface AuthContextType {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  scopes: string[];
  isSuperuser: boolean;
  login: (accessToken: string, refreshToken: string, user: User) => void;
  logout: () => Promise<void>;
  refreshScopes: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Reads only tokens + user from localStorage — scopes are always fetched from the server.
const loadTokensFromStorage = () => ({
  accessToken: localStorage.getItem('accessToken'),
  refreshToken: localStorage.getItem('refreshToken'),
  user: (() => {
    try {
      const raw = localStorage.getItem('user');
      return raw ? (JSON.parse(raw) as User) : null;
    } catch {
      return null;
    }
  })(),
});

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [scopes, setScopes] = useState<string[]>([]);
  const [isSuperuser, setIsSuperuser] = useState(false);

  // Scopes are always fetched from the server — never read from localStorage.
  // isLoading stays true until this resolves on mount, so ProtectedRoute never
  // flashes AccessDenied while the request is in flight.
  const refreshScopes = useCallback(async () => {
    try {
      const response = await api.get('/api/auth/user/');
      const { scopes: newScopes, is_superuser } = response.data as {
        scopes: string[];
        is_superuser: boolean;
      };
      setScopes(newScopes);
      setIsSuperuser(is_superuser);
    } catch (error) {
      console.error('Failed to refresh scopes:', error);
    }
  }, []);

  // Restore tokens + user from localStorage on mount, then fetch fresh scopes.
  // isLoading is cleared only after refreshScopes() settles so the UI never
  // renders with an empty scope array.
  useEffect(() => {
    const stored = loadTokensFromStorage();
    if (stored.accessToken && stored.refreshToken && stored.user) {
      setAccessToken(stored.accessToken);
      setRefreshToken(stored.refreshToken);
      setUser(stored.user);
      refreshScopes().finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [refreshScopes]);

  // Cross-tab + same-tab sync for token changes.
  // On login in another tab: hydrate tokens + fetch fresh scopes from server.
  // On logout in another tab: clear everything.
  useEffect(() => {
    const handleStorageChange = () => {
      const stored = loadTokensFromStorage();
      if (stored.accessToken && stored.refreshToken && stored.user) {
        setAccessToken(stored.accessToken);
        setRefreshToken(stored.refreshToken);
        setUser(stored.user);
        void refreshScopes();
      } else {
        setAccessToken(null);
        setRefreshToken(null);
        setUser(null);
        setScopes([]);
        setIsSuperuser(false);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('auth-change', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-change', handleStorageChange);
    };
  }, [refreshScopes]);

  const login = (newAccessToken: string, newRefreshToken: string, newUser: User) => {
    setAccessToken(newAccessToken);
    setRefreshToken(newRefreshToken);
    setUser(newUser);
    localStorage.setItem('accessToken', newAccessToken);
    localStorage.setItem('refreshToken', newRefreshToken);
    localStorage.setItem('user', JSON.stringify(newUser));
    window.dispatchEvent(new Event('auth-change'));
    void refreshScopes();
  };

  const logout = async () => {
    try {
      if (refreshToken) {
        await api.post('/api/auth/logout/', { refresh: refreshToken });
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
    setScopes([]);
    setIsSuperuser(false);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    window.dispatchEvent(new Event('auth-change'));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        refreshToken,
        isLoading,
        scopes,
        isSuperuser,
        login,
        logout,
        refreshScopes,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
